<template>
  <!-- 入力欄と、他のAI機能への切替メニュー。CareerAI から切り出した葉。
       メニューを開いている間は入力欄と入れ替える（モックの指定） -->
  <div class="chat-input-area" :class="{ 'is-menu-open': isMenuOpen }">
    <CareerAiRateLimitBanner
      v-if="showRateLimitBanner && !isMenuOpen"
      :daily-limit="dailyLimit"
      :is-prime="isPrime"
      @cta-click="$emit('cta-click')"
    />

    <CareerAiServiceMenu
      v-if="isMenuOpen"
      ref="serviceMenu"
      :items="menuItems"
      @select="onSelect"
      @close="closeMenu"
    />

    <div v-else class="input-container">
      <button
        v-if="menuItems.length"
        type="button"
        class="menu-toggle"
        aria-label="メニューを開く"
        ref="toggleButton"
        :disabled="loading"
        @click="openMenu"
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <path d="M12 6v12M6 12h12" />
        </svg>
      </button>

      <input
        ref="inputField"
        type="text"
        class="chat-input-field"
        :value="value"
        :placeholder="placeholder"
        :disabled="inputDisabled"
        @input="$emit('input', $event.target.value)"
        @keydown.enter.exact.prevent="$emit('send')"
        @compositionstart="$emit('composition', true)"
        @compositionend="$emit('composition', false)"
      />

      <button
        class="send-button"
        :class="{ disabled: sendDisabled }"
        :disabled="sendDisabled"
        @click="$emit('send')"
      >
        <img src="/static/img/yagichat/send-icon.svg" alt="送信" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import CareerAiRateLimitBanner from '@/components/CareerAiRateLimitBanner.vue'
import CareerAiServiceMenu from './CareerAiServiceMenu.vue'

type ServiceMenuItem = {
  key: string
  label: string
  description: string
  route: string
}

const props = defineProps({
  value: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  // 本日の上限に達している。入力自体を止める
  inputDisabled: { type: Boolean, default: false },
  showRateLimitBanner: { type: Boolean, default: false },
  dailyLimit: { type: Number, default: 30 },
  isPrime: { type: Boolean, default: false },
  menuItems: { type: Array as () => ServiceMenuItem[], default: () => [] }
})
const emit = defineEmits([
  'input',
  'send',
  'composition',
  'cta-click',
  'menu-select'
])

const inputField = ref<HTMLInputElement | null>(null)
const toggleButton = ref<HTMLButtonElement | null>(null)
const serviceMenu = ref<{ focusClose: () => void } | null>(null)
const isMenuOpen = ref(false)

// ＋と入力欄はメニューと入れ替わりで消えるので、開閉のたびにフォーカスの
// 行き先を明示する（移さないと body へ落ちてキーボードで到達できない）。
// マウス操作で枠が見えないようにするのは :focus-visible 側の担当
const openMenu = (): void => {
  isMenuOpen.value = true
  nextTick(() => serviceMenu.value?.focusClose())
}

const closeMenu = (): void => {
  isMenuOpen.value = false
  nextTick(() => toggleButton.value?.focus())
}

const sendDisabled = computed(
  () => !props.value.trim() || props.loading || props.inputDisabled
)

// 表示中の機能（遷移先が無い項目）は閉じるだけ。
// 選んだ項目ごと消えるので、閉じるボタンと同じくフォーカスを＋へ戻す
const onSelect = (item: ServiceMenuItem): void => {
  closeMenu()
  emit('menu-select', item)
}

// サジェストを押したあとに入力欄へ戻すため、親から呼ぶ最小 API。
// メニューを開いていると入力欄自体が無いので、先に閉じる
const focus = (): void => {
  isMenuOpen.value = false
  nextTick(() => inputField.value?.focus())
}

// 応答が始まったら開いたままにしない（入力欄が消えたままになるため）
watch(
  () => props.loading,
  (value) => {
    if (value) isMenuOpen.value = false
  }
)

defineExpose({ focus })
</script>

<style
  lang="scss"
  scoped
  src="@/assets/scss/career-ai/career-ai-input.scss"
></style>
